import { LazyMotion, domAnimation, m } from "framer-motion";
import dynamic from "next/dynamic";
import styles from "./index.module.scss";
import { GalleryImage } from "@/molecules/ImageCard";
import { MultiPurposeParagraph } from "@/atoms/Paragraph";
import { Shape } from "@/components/Atoms/Spacer";
import { FeaturedTitle } from "@/components/Atoms/Title";
import ImageSlider from "@/components/Molecules/ImageSlider";
import { GalleryText, MultiTextComponent } from "@/components/Atoms/MultiText";
import Image from "@/components/Atoms/Image";
import { ButtonMaster } from "@/components/Atoms/Buttons";
import {
  GalleryHeader,
  getGalleryLabelColor,
  getTextAlignmentItemClasses,
  getTitleLayout,
} from "./gallery-utils";
import { getSectionMarginClasses } from "@/lib/section-margins";
import { resolveDatoImageLabel } from "@/lib/functions";

const GalleryWrapAlbum = dynamic(() => import("./GalleryWrapAlbum"), {
  ssr: false,
});

export default function ImageGallery(data: any) {
  return (
    <LazyMotion features={domAnimation}>
      <m.div
        className="relative"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        exit="exit"
      >
        {data.addSpacer && (
          <Shape
            shape={data.spacerConfiguration?.customShape}
            color={data.bgColor}
            direction={data.spacerConfiguration?.spacerDirection}
            icon={[
              data.spacerConfiguration?.addIconElement,
              data.spacerConfiguration?.spacerIcon,
            ]}
            position={data.spacerConfiguration?.spacerPosition}
            iconPosition={data.spacerConfiguration?.iconPosition}
            invert={false}
          />
        )}
        <div
          style={{ backgroundColor: data?.bgColor?.color.hex }}
          className={`${getSectionMarginClasses({
            useSectionMargins: data.useSectionMargins,
            useExtraBottomMargins: data.useExtraBottomMargins,
          })} flex  gap-6 ${
            data.placeTextFirst ? "flex-col-reverse" : "flex-col"
          } `}
        >
          <div
            className="flex w-full flex-col gap-4 justify-center mx-auto relative"
          >
            {data.layout == "FullWidth" ? (
              <>
                <div className="flex flex-col gap-2 mx-auto relative z-10">
                  <div className="grid grid-cols-2 md:flex flex-row gap-2">
                    {data.gallery.map((entry: any, i: number) => {
                      const imageLabel = resolveDatoImageLabel(entry);
                      return (
                      <div
                        key={i}
                        className={`${styles.galleryImageItem} object-cover`}
                      >
                        <div className={styles.galleryImage}>
                          <GalleryImage
                            options={data.contentBorderRadius}
                            data={entry}
                          />
                        </div>
                        {data.showImageLabel &&
                          !data.useSingleLabel &&
                          imageLabel && (
                          <div className="w-full shrink-0">
                            <MultiPurposeParagraph
                              type="image"
                              data={imageLabel}
                              color={getGalleryLabelColor(data)}
                            />
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  <div className="px-4">
                    {data.showImageLabel && data.useSingleLabel && (
                      <div className="w-full">
                        <MultiPurposeParagraph
                          type="image"
                          data={data.imageLabel}
                          color={getGalleryLabelColor(data)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : data.layout == "Container" ? (
              <div className="flex-col md:flex md:flex-row container justify-center gap-4 md:gap-6 mx-auto relative z-10">
                {data.gallery?.map((entry: any, i: number) => {
                  const ar = entry.responsiveImage.aspectRatio;
                  const imageLabel = resolveDatoImageLabel(entry);
                  const style = {
                    "--ar": ar,
                    flex: `${ar} 1 0`,
                  } as React.CSSProperties;
                  return (
                    <div
                      style={style as React.CSSProperties}
                      className={styles.galleryImageItem}
                      key={i}
                    >
                      <div className={`${styles.galleryImage} md:h-[400px]`}>
                        <GalleryImage
                          options={data.contentBorderRadius}
                          data={entry}
                        />
                      </div>
                      {data.showImageLabel &&
                        !data.useSingleLabel &&
                        imageLabel && (
                        <div className="w-full shrink-0">
                          <MultiPurposeParagraph
                            type="image"
                            data={imageLabel}
                            color={getGalleryLabelColor(data)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                {data.showImageLabel && data.useSingleLabel && (
                  <div className="w-full">
                    <MultiPurposeParagraph
                      data={data.imageLabel}
                      color={getGalleryLabelColor(data)}
                    />
                  </div>
                )}
              </div>
            ) : data.layout == "wrap" ? (
              <GalleryWrapAlbum
                data={data}
                containerClassName="container mx-auto relative z-10 flex flex-col gap-10 md:gap-12"
                defaultContainerWidth={1100}
              />
            ) : data.layout == "wrapContainer" ? (
              <GalleryWrapAlbum
                data={data}
                containerClassName="container mx-auto relative z-10 flex flex-col gap-10 md:gap-12 max-w-[72rem] w-full"
                defaultContainerWidth={1152}
              />
            ) : data.layout == "Slider" ? (
              <>
                {data.swapImageGalleryForCardGallery ? (
                  <ImageSlider
                    breakDesktop={3}
                    sliderClass="px-[5px]"
                    data={data}
                  >
                    {data.galleryCard.map((entry: any, i: number) => {
                      return (
                        <div
                          className={`${styles.bgCard} flex-auto shadow-lg `}
                          key={i}
                        >
                          <GalleryImage
                            options={data.contentBorderRadius}
                            data={entry.asset}
                          />
                          <div className={` flex flex-col p-6`}>
                            <GalleryText
                              data={entry.description}
                              color={data.textColor?.color}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </ImageSlider>
                ) : (
                  <ImageSlider
                    breakDesktop={3}
                    sliderClass="px-[5px]"
                    data={data}
                  >
                    {" "}
                    {data.gallery.map((entry: any, i: number) => {
                      const style = {
                        "--ar": entry.responsiveImage.aspectRatio,
                      } as React.CSSProperties;
                      return (
                        <div
                          style={style as React.CSSProperties}
                          className={`lg:h-[15rem] xl:h-[25rem] flex-auto`}
                          key={i}
                        >
                          <GalleryImage
                            options={data.contentBorderRadius}
                            data={entry}
                          />
                        </div>
                      );
                    })}
                  </ImageSlider>
                )}
              </>
            ) : (
              <div className="max-w-[1360px] mx-auto grid grid-cols-2 gap-y-6 relative z-10 ">
                {(data.title || (data.addLabel && data.label)) && (
                  <div
                    className={`flex flex-col w-fit max-w-full lg:col-start-2 col-span-2 lg:col-span-1 ${getTextAlignmentItemClasses(
                      data.textAlignment
                    )}`}
                  >
                    <GalleryHeader
                      data={data}
                      titleTag="h4"
                      TitleComponent={FeaturedTitle}
                    />
                  </div>
                )}
                <div className="flex flex-wrap gap-4 lg:col-start-2 col-span-2 lg:col-span-1">
                  {data.gallery.map((entry: any, i: number) => {
                    const imageLabel = resolveDatoImageLabel(entry);
                    const style = {
                      "--ar": entry.responsiveImage.aspectRatio,
                    } as React.CSSProperties;
                    return (
                      <div
                        style={style as React.CSSProperties}
                        className={`${styles.galleryImageItem} flex-auto`}
                        key={i}
                      >
                        <div
                          className={`${styles.galleryImageHalf} ${
                            data.gallery.length > 1
                              ? " h-[10rem] lg:h-[15rem] xl:h-[20rem]"
                              : ""
                          }`}
                        >
                          <GalleryImage
                            options={data.contentBorderRadius}
                            data={entry}
                          />
                        </div>
                        {data.showImageLabel &&
                          !data.useSingleLabel &&
                          imageLabel && (
                          <div className="w-full shrink-0">
                            <MultiPurposeParagraph
                              type="image"
                              data={imageLabel}
                              color={getGalleryLabelColor(data)}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {data.showImageLabel && data.useSingleLabel && (
                    <div className="w-full">
                      <MultiPurposeParagraph
                        data={data.imageLabel}
                        color={getGalleryLabelColor(data)}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {data.addText && (
            <div className="w-full container mx-auto relative z-10">
              <article
                className={`mx-auto container flex flex-col gap-12 2xl:max-w-[1360px] ${getTextAlignmentItemClasses(
                  data.textAlignment
                )}`}
              >
                {(data.title || (data.addLabel && data.label)) && (
                  <GalleryHeader data={data} />
                )}
                <div
                  className={`${
                    data.textColumnLayout !== "single"
                      ? `${styles.doubleColumn}`
                      : `${styles.singleColumn}`
                  }`}
                >
                  <MultiTextComponent
                    columns={data.textColumnLayout}
                    data={data.textContent}
                    color={data.textColor?.color}
                    layout={data.textAlignment}
                  />
                </div>
                {data.addDownloadBlock && data.file?.url && (
                  <div
                    className={`flex flex-col justify-center gap-4 w-fit max-w-full ${getTextAlignmentItemClasses(
                      data.textAlignment
                    )}`}
                  >
                    <MultiPurposeParagraph
                      type={"featured"}
                      align={data.textAlignment}
                      data={data.downloadDescription}
                      color={data.textColor?.color}
                    />

                    <ButtonMaster
                      anim={""}
                      type={data?.buttonConfig.buttonStyle}
                      cta={data?.buttonConfig.typeColor?.color}
                      color={data?.buttonConfig.labelColor?.color}
                      customIcon={data?.buttonConfig.icon?.img}
                      customColor={data?.buttonConfig.iconColor?.color}
                    >
                      <a
                        className="w-fit"
                        target="_blank"
                        rel="noreferrer"
                        href={data.file?.url}
                        download={data.downloadDescription}
                      >
                        {data?.buttonConfig?.buttonLabel?.label}
                      </a>
                    </ButtonMaster>
                  </div>
                )}
              </article>
            </div>
          )}
          {data.addBackgroundImage && (
            <Image
              className={`${styles.imageBg} m-auto absolute hidden md:block left-0 h-[420px] w-fit z-[0] bottom-6`}
              data={data.backgroundImage?.img.url}
            />
          )}
        </div>
      </m.div>
    </LazyMotion>
  );
}

export const fragment = `
    fragment SectionGalleryFragment on SectionGalleryRecord{
        __typename
        id
        title
        addLabel
        label
        layout
        placeTextFirst
        addTitleUnderline
        customUnderlineAsset {
          img {
            url
            width
            height
          }
        }
        underlineColor {
          color {
            hex
          }

        }
        textAlignment
        addText
        textColor{
          color{
            hex
          }
        }
        labelColor{
          color{
            hex
          }
        }
        bgColor{
          color{
            hex
          }
        }
    addBackgroundImage
    backgroundImage {
      img {
            url
            width
            height
      }
    }
        addSpacer
        spacerConfiguration {
          id
          spacerDirection
          spacerPosition
          customShape {
            img {
              alt
              url
              width
              height
            }
          }
          addIconElement
          spacerIcon {
            img {
              alt
              url
              width
              height
            }
          }
          iconPosition
        }
        showImageLabel
        useSingleLabel
        imageLabel
        contentBorderRadius
        addDownloadBlock
         buttonConfig{
          ...pageRelatedFragment
         }
        downloadDescription
        downloadLabel {
          label
        }
        file {
          url
          filename
        }
        textContent(markdown: true)
        textColumnLayout
        useSectionMargins
        useExtraBottomMargins
        swapImageGalleryForCardGallery
        galleryCard {
          id
          description
          asset {
            responsiveImage (imgixParams: { fit: crop, crop: focalpoint, auto: format, q: 85, w: 1200 }, sizes: "(max-width: 768px) 100vw, (max-width: 1024px) 90vw, 1200px") {
              ... responsiveImageFragment
            }
          }
        }
        gallery {
          title
          responsiveImage (imgixParams: { fit: crop, crop: focalpoint, auto: format, q: 85, w: 1200 }, sizes: "(max-width: 768px) 100vw, (max-width: 1024px) 90vw, 1200px") {
           ... responsiveImageFragment
          }
        }
    }
`;
